import { arrayField, rawField, stringField } from './jsonBody';
import { caseHref } from './caseHref';
import { SCREEN_NAMES, type ScreenName, type ScreenParams, type TabStack } from '@/types/navigation';

type Tab = keyof TabStack;

export const SCREEN_URL_MAP: Record<ScreenName, (params?: ScreenParams) => string> = {
  balance: () => '/',
  packages: () => '/packages',
  payment: () => '/payment',
  'draws-history': () => '/draws',
  'draw-participants': (p) => `/draws/${encodeURIComponent(String(p?.drawId ?? ''))}`,

  games: () => '/games',
  'rush-mid': () => '/games/rush-mid',
  'rush-mid-match-detail': (p) => `/games/rush-mid/match/${encodeURIComponent(String(p?.matchId ?? ''))}`,
  'rush-mid-open-game': (p) => `/games/rush-mid/game/${encodeURIComponent(String(p?.gameId ?? ''))}`,
  'rush-mid-spectate': (p) => `/games/rush-mid/spectate/${encodeURIComponent(String(p?.matchId ?? ''))}`,
  'rush-mid-replay': (p) => `/games/rush-mid/replay/${encodeURIComponent(String(p?.matchId ?? ''))}`,
  'rush-mid-raffle-round': (p) => `/games/rush-mid/raffle/${encodeURIComponent(String(p?.roundId ?? ''))}`,
  'rush-mid-raffle-winners': () => '/games/rush-mid/raffle/winners',
  'rush-mid-survey': () => '/games/rush-mid-survey',
  roulette: () => '/games/roulette',
  'roulette-match': (p) => `/games/roulette/match/${encodeURIComponent(String(p?.matchId ?? ''))}`,
  cases: () => '/cases',
  'case-details': (p) => caseHref({ id: String(p?.caseId ?? ''), slug: stringField(p, 'caseSlug') }),
  'case-item': (p) =>
    `/cases/${encodeURIComponent(String(p?.caseId ?? ''))}/items/${encodeURIComponent(String(p?.hashName ?? ''))}`,
  'case-roulette': (p) => `/cases/${encodeURIComponent(String(p?.caseId ?? ''))}/open`,
  'case-honeycomb': (p) =>
    `/cases/${encodeURIComponent(String(p?.caseId ?? ''))}/open/multi?count=${encodeURIComponent(String(p?.count ?? 2))}`,
  'case-open-results': (p) => {
    const ids = (arrayField(p, 'dropIds') ?? []).map(String);
    return `/cases/${encodeURIComponent(String(p?.caseId ?? ''))}/results?ids=${encodeURIComponent(ids.join(','))}`;
  },
  shop: () => '/shop',
  'shop-skins': (p) => {
    const qs: string[] = [];
    for (const k of ['weapon', 'category', 'weaponImage'] as const) {
      const v = p?.[k];
      if (v !== undefined && v !== null && v !== '') {
        qs.push(`${k}=${encodeURIComponent(String(v))}`);
      }
    }
    return `/shop/skins${qs.length ? `?${qs.join('&')}` : ''}`;
  },
  'shop-items': (p) => {
    const segment = p?.category ?? p?.categoryId ?? p?.type ?? 'all';
    const qs: string[] = [];
    for (const k of ['weapon', 'skinName', 'type'] as const) {
      const v = p?.[k];
      if (v !== undefined && v !== null && v !== '' && k !== 'type') {
        qs.push(`${k}=${encodeURIComponent(String(v))}`);
      } else if (k === 'type' && v && v !== segment) {
        qs.push(`${k}=${encodeURIComponent(String(v))}`);
      }
    }
    return `/shop/${encodeURIComponent(String(segment))}${qs.length ? `?${qs.join('&')}` : ''}`;
  },
  'shop-item': (p) => {
    const item = rawField(p, 'item');
    const hashName = stringField(item, 'hashName') ?? stringField(p, 'hashName') ?? '';
    const segment =
      stringField(item, 'category') ?? stringField(p, 'category') ??
      stringField(item, 'type') ?? stringField(p, 'type') ?? 'unknown';
    return `/shop/${encodeURIComponent(segment)}/${encodeURIComponent(hashName)}`;
  },
  upgrade: () => '/upgrade',
  'case-battles': () => '/case-battles',
  'case-battle-create': () => '/case-battles/create',
  'case-battle-details': (p) => `/case-battles/${encodeURIComponent(String(p?.battleId ?? ''))}`,
  'case-battle-history': () => '/case-battles/history',
  'giveaway-details': (p) => `/giveaways/${encodeURIComponent(String(p?.giveawayId ?? ''))}`,
  'daily-giveaway-details': (p) => `/daily-giveaways/${encodeURIComponent(String(p?.giveawayId ?? ''))}`,
  rains: () => '/rains',
  'rain-details': (p) => `/rains/${encodeURIComponent(String(p?.rainId ?? ''))}`,
  'daily-giveaway-details-winners': (p) =>
    `/daily-giveaways/${encodeURIComponent(String(p?.giveawayId ?? ''))}/winners`,

  inventory: (p) => (p?.filter ? `/inventory?filter=${encodeURIComponent(String(p.filter))}` : '/inventory'),
  'inventory-item': (p) => `/inventory/${encodeURIComponent(String(p?.itemId ?? ''))}`,
  'send-item': (p) => `/inventory/${encodeURIComponent(String(p?.itemId ?? ''))}/send`,

  contracts: () => '/contracts',

  referrals: () => '/referrals',
  'referral-contest': () => '/referrals/contest',

  band: () => '/band',
  'band-shelf': () => '/band/shelf',
  'band-raffle-round': (p) => `/band/raffle/${encodeURIComponent(String(p?.roundId ?? ''))}`,
  'band-raffle-winners': () => '/band/raffle/winners',
  'band-race': () => '/band/race',
  'band-race-winners': () => '/band/race/winners',
  'band-final': () => '/band/final',
  'band-final-winners': () => '/band/final/winners',

  profile: (p) => (p?.userId ? `/profile/${encodeURIComponent(String(p.userId))}` : '/profile'),
  achievements: () => '/achievements',
  'steam-inventory': (p) =>
    p?.userId ? `/users/${encodeURIComponent(String(p.userId))}/steam-inventory` : '/steam-inventory',
  'cs2-link': (p) => (p?.step ? `/cs2-matches/link?step=${encodeURIComponent(String(p.step))}` : '/cs2-matches/link'),
  'cs2-matches': () => '/cs2-matches',
  settings: () => '/settings',
  'trade-link': () => '/settings/trade-link',
  promocode: (p) => (p?.code ? `/promocode?code=${encodeURIComponent(String(p.code))}` : '/promocode'),
  wheel: () => '/wheel',
  language: () => '/settings/language',
  'level-info': () => '/help/level',
  'xp-history': () => '/xp/history',
  'recruiter-guide': () => '/help/recruiter',

  onboarding: () => '/onboarding/welcome',
  'onboarding-item-reveal': () => '/onboarding/item-reveal',
  'onboarding-tasks': () => '/onboarding/tasks',
  'onboarding-skin-decision': () => '/onboarding/skin-decision',
  'onboarding-friends': () => '/onboarding/friends',
  'onboarding-bonus': () => '/onboarding/bonus',
};

export const TAB_URL_MAP: Record<Tab, string> = {
  balance: '/home',
  cases: '/cases',
  games: '/games',
  inventory: '/inventory',
  referrals: '/referrals',
  profile: '/profile',
  shop: '/shop',
  onboarding: '/onboarding/welcome',
};

const TAB_BY_PREFIX: ReadonlyArray<readonly [string, Tab]> = [
  ['/onboarding', 'onboarding'],
  ['/home', 'balance'],
  ['/cases', 'cases'],
  ['/games', 'games'],
  ['/case-battles', 'games'],
  ['/daily-giveaways', 'games'],
  ['/rains', 'games'],
  ['/giveaways', 'games'],
  ['/upgrade', 'games'],
  ['/shop', 'shop'],
  ['/inventory', 'inventory'],
  ['/contracts', 'games'],
  ['/referrals', 'referrals'],
  ['/profile', 'profile'],
  ['/settings', 'profile'],
  ['/promocode', 'profile'],
  ['/wheel', 'profile'],
  ['/xp', 'profile'],
  ['/help', 'profile'],
  ['/achievements', 'profile'],
  ['/steam-inventory', 'profile'],
  ['/cs2-matches', 'profile'],
  ['/band', 'profile'],
  ['/payment', 'balance'],
  ['/packages', 'balance'],
  ['/draws', 'balance'],
];

export function pathnameToTab(pathname: string): Tab {
  for (const [prefix, tab] of TAB_BY_PREFIX) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return tab;
  }
  return 'balance';
}

const PROBE_VALUES: ScreenParams = {
  caseId: '__caseId__',
  hashName: '__hashName__',
  itemId: '__itemId__',
  drawId: '__drawId__',
  battleId: '__battleId__',
  roundId: '__roundId__',
  giveawayId: '__giveawayId__',
  rainId: '__rainId__',
  partnerId: '__partnerId__',
  category: '__category__',
  weapon: '__weapon__',
  type: '__type__',
  skinName: '__skinName__',
  userId: '__userId__',
  code: '__code__',
  filter: '__filter__',
};

const PROBE_TOKEN_RE = /__[a-zA-Z]+__/g;

interface ReverseEntry {
  re: RegExp;
  screen: ScreenName;
}

const { exact: EXACT_REVERSE, dynamic: DYNAMIC_REVERSE } = ((): {
  exact: Record<string, ScreenName>;
  dynamic: ReverseEntry[];
} => {
  const exact: Record<string, ScreenName> = {};
  const dynamic: ReverseEntry[] = [];

  for (const screen of SCREEN_NAMES) {
    let probed: string;
    try {
      probed = SCREEN_URL_MAP[screen](PROBE_VALUES);
    } catch {
      continue;
    }
    const pathname = probed.split('?')[0];
    if (!PROBE_TOKEN_RE.test(pathname)) {
      if (!exact[pathname]) exact[pathname] = screen;
      continue;
    }
    PROBE_TOKEN_RE.lastIndex = 0;
    const pattern =
      '^' + pathname.replace(PROBE_TOKEN_RE, '[^/]+').replace(/\//g, '\\/') + '$';
    dynamic.push({ re: new RegExp(pattern), screen });
  }

  dynamic.sort((a, b) => b.re.source.length - a.re.source.length);

  return { exact, dynamic };
})();

export function pathnameToScreen(pathname: string): ScreenName | null {
  if (EXACT_REVERSE[pathname]) return EXACT_REVERSE[pathname];
  for (const { re, screen } of DYNAMIC_REVERSE) {
    if (re.test(pathname)) return screen;
  }
  return null;
}

const ORPHAN_KNOWN_PATHS = new Set<string>([
  '/login',
  '/login/connect',
  '/login/link-telegram',
  '/login/steam-linked',
  '/login/telegram-linked',
  '/payment/success',
  '/payment/fail',
  '/exchange',
]);

export function isKnownScreenPath(pathname: string): boolean {
  if (ORPHAN_KNOWN_PATHS.has(pathname)) return true;
  return pathnameToScreen(pathname) !== null;
}

export function buildScreenUrl(screen: ScreenName, params?: ScreenParams): string {
  return SCREEN_URL_MAP[screen](params);
}
